/**
 * Privacy Policy — static legal page, promised to Intuit as part of the
 * QuickBooks Online app-review submission. Registered at exactly
 * /legal/privacy (see AppRoutes.tsx). Rendered OUTSIDE the ClickPay auth
 * wall (see App.tsx) — Intuit's review team and end users must be able to
 * load this without a ClickPay session.
 *
 * Plain-English content describing the QBO scope ClickPay actually
 * requests (read-only accounting data for payment reconciliation) — keep
 * this in sync with the real OAuth scopes/backend calls if those change.
 */
export default function LegalPrivacy() {
  return (
    <div className="min-h-screen bg-gray-50 flex justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-xl font-semibold text-gray-900">ClickPay</h1>
          <p className="text-xs text-gray-500 mt-0.5">Privacy Policy</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-sm text-gray-700 leading-relaxed space-y-5">
          <p className="text-xs text-gray-400">Last updated: July 2026</p>

          <p>
            ClickPay is an internal operations tool built and operated by
            BetterBasics (MeasuRite) to run its own Amazon marketplace
            operations, advertising (PPC), and accounting. This page
            explains what happens to data when ClickPay is connected to a
            QuickBooks Online account.
          </p>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              What we access
            </h2>
            <p>
              When QuickBooks Online is connected, ClickPay requests
              read-only access to accounting data related to supplier
              bills and payments, specifically:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Bills and bill payments</li>
              <li>Purchases</li>
              <li>Associated vendor names, amounts, dates, and memo/description text</li>
            </ul>
            <p className="mt-2">
              ClickPay does not request access to payroll, banking
              credentials, customer/sales data, or any QuickBooks data
              beyond what's listed above.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              Why we access it
            </h2>
            <p>
              This data is used to reconcile supplier payments recorded in
              QuickBooks against BetterBasics/MeasuRite's own purchase
              orders — matching what was billed and paid to what was
              ordered, so the operator can see accurate, up-to-date
              supplier payment status inside ClickPay without manually
              cross-checking two systems.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              Where it's stored
            </h2>
            <p>
              Retrieved data is stored in ClickPay's own database,
              operated by BetterBasics/MeasuRite. It is used solely to
              power ClickPay's own features (reconciliation views,
              reporting, and related automation) and is not sold, shared,
              or transferred to any third party.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              How to revoke access
            </h2>
            <p>
              You can disconnect QuickBooks from ClickPay at any time in
              two ways:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                From ClickPay's Integrations settings, click Disconnect
                next to QuickBooks Online.
              </li>
              <li>
                Directly from your Intuit account, under Connected Apps —
                revoking access there immediately stops ClickPay's access
                regardless of ClickPay's own settings.
              </li>
            </ul>
            <p className="mt-2">
              After disconnecting, ClickPay stops pulling new data from
              QuickBooks; previously retrieved reconciliation data already
              stored in ClickPay's database is retained until removed by
              the app owner.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              Contact
            </h2>
            <p>
              Questions about this policy — contact the app owner. There
              is no dedicated support team; ClickPay is maintained
              directly by BetterBasics/MeasuRite. See also the{' '}
              <a href="/legal/eula" className="text-brand-600 hover:underline">
                End User License Agreement
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
